import { Button, Heading, Text, HStack, Stack, VStack } from '@genial/components';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, Radio } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { useResetRecoilState } from 'recoil';

import { LateralBar } from '../../../../../../../../../../components/LateralBar';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../../../../../routes/types';
import { privatePensionOrderState } from '../../../../../../../../../../store/product/atom';
import { AssetSelectedTypeMap } from '../../../../../../../../../../store/product/types';
import { PrivatePensionKind } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';
import { PrivatePensionModalHeader } from '../PrivatePensionModalHeader';

export namespace PrivatePensionKindSelectorModalTypes {
  export interface Props {
    onClose: () => void;
    productType: string;
    offer: AssetSelectedTypeMap['privatePension'];
    accountId: number;
    accountNumber: number;
    customerId: number;
    cpfCnpj: string;
  }
}

export const PrivatePensionKindSelectorModal: React.FC<
  PrivatePensionKindSelectorModalTypes.Props
> = ({ onClose, offer, accountId, accountNumber, customerId, cpfCnpj }) => {
  const [currentKind, setCurrentKind] = useState<PrivatePensionKind>(PrivatePensionKind.PGBL);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const resetState = useResetRecoilState(privatePensionOrderState);

  const handleChangeKind = (value: string) => {
    setCurrentKind(Number(value) as PrivatePensionKind);
  };

  const handleConfirm = () => {
    navigation.navigate('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'PrivatePensionApplication',
        params: {
          productType: 'privatePension',
          kind: currentKind,
          offer,
          accountId,
          accountNumber,
          customerId,
          cpfCnpj,
        },
      },
    });
  };

  const handleCancel = () => {
    onClose();
  };

  const options = useMemo(() => {
    return [
      {
        title: 'PGBL - Plano Gerador de Beneficio Livre',
        label:
          'Plano de previdência privada a longo prazo, destinado principalmente á aposentadoria. O PGBL, indicado para quem entrega a declaração completa do IR, funciona como fundo de investimentos e permite acúmulo de aplicações, por um prazo pré-determinado. Os recursos são investidos e rentabilizados pela seguradora e é possível abater 12% da renda total tributável.',
        value: PrivatePensionKind.PGBL,
      },
      {
        title: 'VGBL - Vida Gerador de Benefício Livre',
        label:
          'Plano de previdência privada ideal para quem entrega a declaração simplificada do IR, já que não há dedução do IR no período de aplicação e acumulação do dinheiro. Os tributos só são cobrados sobre os rendimentos e não sobre o montante total. O investimento é indicado também para planejamentos sucessórios, pois não passa por inventário e os recursos são transferidos automaticamente.',
        value: PrivatePensionKind.VGBL,
      },
    ];
  }, []);

  useEffect(() => {
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VStack width="100%" space="stack.xxxs">
      <PrivatePensionModalHeader title="Nova Aplicação" onClose={handleCancel} />
      <Box padding="stack.xxs" width="100%">
        <LateralBar barColor="product.previdencia" />
        <VStack space="stack.xxxs" width="100%">
          <Heading size="h3" testId="private-pension-kind-selector-title">
            {offer.productNameNormalized}
          </Heading>
          <Radio.Group
            onChange={handleChangeKind}
            name="privatePensionKind"
            value={currentKind.toString()}>
            {options.map((option, index) => {
              const isSelected = currentKind === option.value;
              const borderColor = isSelected ? 'brand.tertiary.light' : 'transparent';
              const backgroundColor = isSelected
                ? Utils.ColorToken.WithOpacity('feedback.gen.lightest', 0.65)
                : 'background';

              return (
                <Box
                  key={option.value.toString()}
                  width="100%"
                  padding="inset.xs"
                  backgroundColor={backgroundColor}
                  borderRadius="sm"
                  borderColor={borderColor}
                  borderWidth={1}
                  marginBottom={index === options.length - 1 ? 0 : 'stack.xxxs'}>
                  <VStack flex={1} space="stack.xxxs">
                    <HStack alignItems="center" justifyContent="space-between">
                      <Text fontWeight="bold">{option.title}</Text>
                      <Radio value={option.value.toString()} />
                    </HStack>
                    <Text fontSize="sm">{option.label}</Text>
                  </VStack>
                </Box>
              );
            })}
          </Radio.Group>
        </VStack>
      </Box>
      <Stack
        direction={{ base: 'column', md: 'row' }}
        width="100%"
        justifyContent="center"
        space="stack.nano"
        flexDirection={{ base: 'column-reverse', md: 'row' }}>
        <Button variant="secondary" onPress={handleCancel} flex={1}>
          Cancelar
        </Button>
        <Button variant="callToAction" onPress={handleConfirm} flex={1}>
          Contratar
        </Button>
      </Stack>
    </VStack>
  );
};
